import { useContext, useMemo } from "react";
import { Box, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import NoData from "components/NoData/NoData.view";
import { useStyles } from "./DowntimeSummaryByProductionLine.styles";
import { GroupDataByProductionLine } from "./DowntimeSummaryByProductionLineInterfaces";
import { UptimeDowntimeBar } from "./UptimeDowntimeBar/UptimeDowntimeBar.view";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { allElements } from "constants/constants";
import { truncateLongText } from "utilities/utilities";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";

const DowntimeSummaryByProductionLine = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        filters,
        showAllProductionLines,
        handleShowAllProductionLinesChange,
        handleSelectedProductionLineChange,
        selectedProductionLine,
    } = useContext(SummaryReportDashboardContext);
    const { millId } = useParams();

    const {
        displayUnit: displayIn,
        reportRangeEndDate: endDate,
        reportRangeStartDate: startDate,
    } = useContext(MillDashboardContext);

    const { productionLineSummary } = MillsEntity.GetUptimesVsDowntimesTotalsPerMachineCenterMillDashboard(
        { ...filters, startDate, endDate, displayIn },
        Number(millId),
    );

    const groupedDataByProductionLine: GroupDataByProductionLine = useMemo(
        () =>
            productionLineSummary.reduce((groups: GroupDataByProductionLine, obj: any) => {
                const productionLineId = obj.production_line_id;
                const productionLineName = obj.production_line.code || obj.production_line.name;

                if (!groups[productionLineId]) {
                    groups[productionLineId] = {
                        productionLineId: productionLineId,
                        productionLineName: productionLineName,
                        downtime: {
                            seconds: 0,
                            occurrences: 0,
                        },
                        uptime: {
                            seconds: 0,
                            occurrences: 0,
                        },
                    };
                }

                groups[productionLineId].downtime.seconds += obj.downtime_seconds;
                groups[productionLineId].downtime.occurrences += obj.downtime_occurrences;
                groups[productionLineId].uptime.seconds += obj.uptime_seconds;
                groups[productionLineId].uptime.occurrences += obj.uptime_occurrences;

                return groups;
            }, {}),
        [productionLineSummary],
    );

    return Object.values(groupedDataByProductionLine).length > 0 ? (
        <Box className={classes.mainWrapper}>
            <section className={classes.leftSection}>
                {Object.values(groupedDataByProductionLine).length >= 1 && (
                    <div>
                        {Object.values(groupedDataByProductionLine).map((machineCenter, index) => {
                            return (
                                <UptimeDowntimeBar
                                    key={`${index + Math.random()}`}
                                    onClick={() =>
                                        handleSelectedProductionLineChange(
                                            machineCenter.productionLineId,
                                            machineCenter.productionLineName,
                                        )
                                    }
                                    selected={selectedProductionLine === machineCenter.productionLineId}
                                    allProductionLinesSelected={showAllProductionLines}
                                    productionLineName={truncateLongText(machineCenter.productionLineName, 12)}
                                    uptimeValue={machineCenter.uptime.seconds / 60}
                                    downtimeValue={machineCenter.downtime.seconds / 60}
                                />
                            );
                        })}
                    </div>
                )}
                <section className={classes.optionWrapper}>
                    <FormGroup>
                        <FormControlLabel
                            className={classes.checkboxLabel}
                            control={
                                <Checkbox
                                    color="info"
                                    checked={showAllProductionLines}
                                    onChange={(_, checked) => {
                                        handleShowAllProductionLinesChange(checked);
                                        if (checked) {
                                            handleSelectedProductionLineChange(allElements, "");
                                        } else {
                                            handleSelectedProductionLineChange(
                                                Object.values(groupedDataByProductionLine)[0].productionLineId,
                                                Object.values(groupedDataByProductionLine)[0].productionLineName,
                                            );
                                        }
                                    }}
                                />
                            }
                            label={t(
                                "locations.summary_report_dashboard.downtime_summary_by_production_line.select_all_production_lines",
                            )}
                        />
                    </FormGroup>
                </section>
            </section>
        </Box>
    ) : (
        <NoData className={classes.noData} />
    );
};

export default DowntimeSummaryByProductionLine;
